import { ExecutionModes, VerificationStatus } from "shared/types";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { ReportHeader } from "../../../components/ReportHeader";
import { DefinedQueryObserverResult, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ApplicantSettings, VerificationNames } from "shared/types";
import { Applicant, Application } from "types/application";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { CreditReportByStatus } from "./CreditReportByStatus";
import api from "api/landlord/applications";

export const CreditReportSliceWrapper = ({
  applicant,
  defaultVerificationId,
  screeningCriteria,
  applicationMeta,
  isIdentityVerificationOverrideRequired
}: {
  applicant: Applicant;
  defaultVerificationId: number;
  screeningCriteria?: ApplicantSettings;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
  isIdentityVerificationOverrideRequired: boolean;
}) => {
  const [openRerunModal, setOpenRerunModal] = useState(false);
  const [selectedVerificationId, setSelectedVerificationId] = useState<number>(
    defaultVerificationId
  );
  const allCreditVerifications = applicant.credit_report_verifications;
  const selectedVerification = allCreditVerifications?.find(
    (cr) => cr.id === selectedVerificationId
  );

  const tabs: Tab<number>[] = (allCreditVerifications ?? []).map((cr, idx) => {
    const reportNumber = (allCreditVerifications?.length ?? 0) - idx;
    return {
      value: cr.id,
      label: (
        <span className="flex items-center gap-2">
          Report {reportNumber}
          <Badge
            className="flex items-center capitalize"
            size={BadgeSize.SM}
            variant={!cr.archived_at ? BadgeVariant.SUCCESS : BadgeVariant.BASE}
          >
            {!cr.archived_at ? "Actual" : "Archive"}
          </Badge>
        </span>
      )
    };
  });

  const activeCreditReport = allCreditVerifications?.find(
    (cr) => !cr.archived_at
  );

  const { data: verificationCreditReport, isFetched } = useQuery<
    VerificationReport<CreditReport>
  >({
    queryKey: [
      "CREDIT_REPORT",
      applicant.application_id,
      selectedVerificationId,
      applicant.id
    ],
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          selectedVerificationId
        )
        .then((res) => res.verification)
  });

  const disabledRerunModalTooltip = useMemo(() => {
    if (activeCreditReport?.id !== selectedVerificationId)
      return "Please select the latest credit report to re-run";
    if (
      activeCreditReport?.status === VerificationStatus.Pending ||
      activeCreditReport?.status === VerificationStatus.Processing
    )
      return "Cannot re-run credit report until active report finishes";
  }, [isFetched, activeCreditReport, selectedVerificationId]);

  return (
    <div className="flex h-full flex-col">
      <ReportHeader
        applicationStatus={applicationMeta.data?.status}
        title="Credit report"
        applicantId={applicant.id}
        applicationId={applicant.application_id}
        name={VerificationNames.CreditReport}
        onRerunReport={isFetched ? () => setOpenRerunModal(true) : undefined}
        rerunRequirements={
          isFetched ? selectedVerification?.rerun_requirements : undefined
        }
        hidePrint={
          selectedVerification?.status === VerificationStatus.Pending &&
          selectedVerification.settings?.execution_mode ===
            ExecutionModes.Manual
        }
        disabledRerunModalTooltip={disabledRerunModalTooltip}
      />

      <div className="-mt-2 mb-8 border-b-1 max-w-[90vw] md:max-w-[80vw] lg:max-w-[min(60vw,900px)]">
        {allCreditVerifications && allCreditVerifications.length > 1 && (
          <Tabs
            tabs={tabs}
            active={selectedVerificationId}
            onChange={setSelectedVerificationId}
            size="sm"
            tabStyle="line"
          />
        )}
      </div>

      <div className="flex-1">
        <CreditReportByStatus
          verificationCreditReport={verificationCreditReport}
          isFetched={isFetched}
          applicant={applicant}
          applicationMeta={applicationMeta}
          screeningCriteria={screeningCriteria}
          selectedVerificationId={selectedVerificationId}
          activeReportId={activeCreditReport?.id}
          openRerunModal={openRerunModal}
          setOpenRerunModal={setOpenRerunModal}
          isIdentityVerificationOverrideRequired={
            isIdentityVerificationOverrideRequired
          }
        />
      </div>
    </div>
  );
};
